import {
  createContext, useCallback, useContext, useMemo, useRef, useState,
  type ReactNode,
} from 'react'

export type ToastVariant = 'success' | 'error' | 'info' | 'warning'

export interface ToastAction { label: string; onClick: () => void }

export interface ToastOptions {
  title?: string
  duration?: number
  /** One follow-up action on the toast, e.g. "Undo" after a soft delete (E-UX). */
  action?: ToastAction
}

export interface Toast {
  id: number
  variant: ToastVariant
  message: string
  title?: string
  duration: number
  action?: ToastAction
}

type ShowFn = (message: string, opts?: ToastOptions) => void

interface ToastContextValue {
  toasts: Toast[]
  dismiss: (id: number) => void
  success: ShowFn
  error: ShowFn
  info: ShowFn
  warning: ShowFn
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

const MAX_TOASTS = 4
const DEFAULT_DURATION = 4000

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const idRef = useRef(0)

  const dismiss = useCallback((id: number) => {
    setToasts((cur) => cur.filter((t) => t.id !== id))
  }, [])

  const push = useCallback(
    (variant: ToastVariant, message: string, opts?: ToastOptions) => {
      const id = ++idRef.current
      const t: Toast = {
        id,
        variant,
        message,
        title: opts?.title,
        // A toast with an action stays long enough to use it.
        duration: opts?.duration ?? (opts?.action ? 8000 : DEFAULT_DURATION),
        action: opts?.action,
      }
      setToasts((cur) => {
        const next = [...cur, t]
        // cap at MAX_TOASTS by dropping the oldest
        return next.length > MAX_TOASTS ? next.slice(next.length - MAX_TOASTS) : next
      })
      if (t.duration > 0) {
        window.setTimeout(() => dismiss(id), t.duration)
      }
    },
    [dismiss],
  )

  const value = useMemo<ToastContextValue>(
    () => ({
      toasts,
      dismiss,
      success: (m, o) => push('success', m, o),
      error:   (m, o) => push('error',   m, o),
      info:    (m, o) => push('info',    m, o),
      warning: (m, o) => push('warning', m, o),
    }),
    [toasts, dismiss, push],
  )

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
}

export function useToast(): Omit<ToastContextValue, 'toasts' | 'dismiss'> {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    // Soft-fallback so a missing provider doesn't crash the app at runtime
    // (e.g. during isolated component tests). Logs via console.warn.
    return {
      success: (m) => console.warn('[toast/success]', m),
      error:   (m) => console.warn('[toast/error]', m),
      info:    (m) => console.warn('[toast/info]', m),
      warning: (m) => console.warn('[toast/warning]', m),
    }
  }
  return ctx
}

/** Internal hook used by <Toaster /> to render the active toast queue. */
export function useToastQueue(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToastQueue must be used inside <ToastProvider>')
  return ctx
}
